import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import OrganizationContext from '../../context/OrganizationContext';
import { FaUser, FaCog, FaSignOutAlt, FaChevronDown, FaClipboardList, FaChartLine, FaUserShield, FaQuestionCircle, FaTachometerAlt, FaLightbulb, FaBookOpen, FaRoad } from 'react-icons/fa';
import LogoImage from '../common/LogoImage';
import HelpModal from '../help/HelpModal';

const Header = () => {
  const { t } = useTranslation();
  const { user, setIsAuthenticated, setUser, setShowTutorial } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { currentOrganization } = useContext(OrganizationContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get organization name from context or fallback to user data
  const currentOrgName = currentOrganization?.name || user?.organizationName || user?.organization?.name || '';

  const handleLogout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');
    
    // Clear auth state
    setIsAuthenticated(false);
    setUser(null);
    
    // Redirect to landing page instead of login
    navigate('/');
  };

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Note: Organization data is now handled by OrganizationContext
  // We no longer need to fetch organization data in the Header component

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleHelpModal = () => {
    setHelpModalOpen(!helpModalOpen);
  };
  
  const startTutorial = () => {
    setShowTutorial(true);
  };
  
  // Determine if we should show the tutorial button
  const showTutorialButton = () => {
    // Only show on dashboard and other main pages, not on landing page
    return user && location.pathname !== '/';
  };

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 20px',
      backgroundColor: '#243444',
      color: '#FFFAFA',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      width: '100%'
    },

    logoContainer: {
      display: 'flex',
      alignItems: 'center'
    },
    navLinks: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: '20px'
    },
    navLink: {
      display: 'flex',
      alignItems: 'center',
      color: 'var(--secondary-color)',
      textDecoration: 'none',
      padding: '5px 10px',
      marginRight: '15px',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease',
      fontWeight: 'bold',
      fontSize: '14px'
    },
    navLinkHover: {
      backgroundColor: 'rgba(210, 186, 123, 0.1)'
    },
    navLinkIcon: {
      marginRight: '5px'
    },
    logo: {
      height: '20px',
      marginRight: '15px',
      width: 'auto',
      display: 'block'
    },
    title: {
      color: '#D2BA7B',
      margin: 0,
      fontSize: '1.2rem',
      fontWeight: 'bold'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    userName: {
      color: 'var(--header-text)',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer'
    },
    userOrg: {
      color: 'var(--secondary-color)',
      fontSize: '0.9rem'
    },
    chevron: {
      marginLeft: '5px',
      fontSize: '12px',
      transition: 'transform 0.3s ease'
    },
    chevronOpen: {
      transform: 'rotate(180deg)'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      right: '20px',
      backgroundColor: 'var(--dropdown-background)',
      borderRadius: '4px',
      boxShadow: '0 2px 10px var(--shadow-color)',
      width: '200px',
      zIndex: 1000,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      opacity: 0,
      visibility: 'hidden',
      transform: 'translateY(-10px)'
    },
    dropdownOpen: {
      opacity: 1,
      visibility: 'visible',
      transform: 'translateY(0)'
    },
    dropdownItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 15px',
      color: 'var(--dropdown-text)',
      textDecoration: 'none',
      borderBottom: '1px solid var(--border-color)',
      transition: 'background-color 0.2s ease'
    },
    dropdownItemHover: {
      backgroundColor: 'var(--dropdown-hover)'
    },
    dropdownIcon: {
      marginRight: '10px',
      fontSize: '16px',
      color: 'var(--dropdown-text)'
    },
    logoutItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 15px',
      color: 'var(--danger-color)',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    }
  };

  return (
    <header style={styles.header}>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <div style={styles.logoContainer}>
          <Link to="/dashboard">
            <LogoImage 
              type="white" 
              style={styles.logo} 
            />
          </Link>
          <h1 style={styles.title}>{t('app.title')}</h1>
        </div>
        
        {/* Quick navigation links */}
        {user && (
          <div style={styles.navLinks}>
            <Link 
              to="/dashboard" 
              style={styles.navLink}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(210, 186, 123, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FaTachometerAlt style={styles.navLinkIcon} />
              Dashboard
            </Link>
            <Link 
              to="/assessments" 
              style={styles.navLink}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(210, 186, 123, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FaClipboardList style={styles.navLinkIcon} />
              {t('nav.assessments')}
            </Link>
            <Link 
              to="/implementation-roadmap" 
              style={styles.navLink}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(210, 186, 123, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FaRoad style={styles.navLinkIcon} />
              Roadmap
            </Link>
          </div>
        )}
        

      </div>
      <div style={styles.userInfo}>
        {user && (
          <>
          </>
        )}
        {user && (
          <>
            <span style={styles.userOrg}>{currentOrgName || 'Bank One'}</span>
            
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '5px' }}>
              {showTutorialButton() && (
                <button
                  className="help-icon-button"
                  onClick={startTutorial}
                  title="Start Tutorial"
                  style={{ color: '#D2BA7B', marginRight: '5px' }}
                >
                  <FaLightbulb className="help-icon" />
                </button>
              )}
              <button
                className="help-icon-button"
                onClick={toggleHelpModal}
                title="Help & FAQ"
              >
                <FaQuestionCircle className="help-icon" />
              </button>
            </div>
            
            <div ref={dropdownRef}>
              <div 
                style={styles.userName} 
                onClick={toggleDropdown}
              >
                {t('nav.welcome')}, {user.name}
                <FaChevronDown 
                  style={{
                    ...styles.chevron,
                    ...(dropdownOpen ? styles.chevronOpen : {})
                  }} 
                />
              </div>
              
              <div 
                className="header-dropdown"
                style={{
                  ...styles.dropdown,
                  ...(dropdownOpen ? styles.dropdownOpen : {})
                }}>
                <Link 
                  to="/profile" 
                  className="header-dropdown-item"
                  style={styles.dropdownItem}
                  onClick={() => setDropdownOpen(false)}
                >
                  <FaUser className="header-dropdown-icon" style={styles.dropdownIcon} />
                  {t('nav.profile')}
                </Link>
                <Link 
                  to="/organization-details" 
                  className="header-dropdown-item"
                  style={styles.dropdownItem}
                  onClick={() => setDropdownOpen(false)}
                >
                  <FaCog className="header-dropdown-icon" style={styles.dropdownIcon} />
                  {t('nav.organizationDetails')}
                </Link>
                <Link 
                  to="/settings" 
                  className="header-dropdown-item"
                  style={styles.dropdownItem}
                  onClick={() => setDropdownOpen(false)}
                >
                  <FaCog className="header-dropdown-icon" style={styles.dropdownIcon} />
                  {t('nav.settings')}
                </Link>
                {user && user.role === 'admin' && (
                  <Link 
                    to="/admin" 
                    className="header-dropdown-item"
                    style={styles.dropdownItem}
                    onClick={() => setDropdownOpen(false)}
                  >
                    <FaUserShield className="header-dropdown-icon" style={{...styles.dropdownIcon, color: '#D2BA7B'}} />
                    {t('nav.adminDashboard')}
                  </Link>
                )}
                <div 
                  className="header-dropdown-item header-logout-item"
                  style={styles.logoutItem}
                  onClick={() => {
                    handleLogout();
                    setDropdownOpen(false);
                  }}
                >
                  <FaSignOutAlt className="header-dropdown-icon" style={styles.dropdownIcon} />
                  {t('nav.logout')}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Help Modal */}
      <HelpModal isOpen={helpModalOpen} onClose={toggleHelpModal} />
    </header>
  );
};

export default Header;
